import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface FeatureSet {
  id: string;
  client_id: string;
  name: string;
  permissions: string[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export type AvailablePermissions = string[];

@Injectable({
  providedIn: 'root',
})
export class FeatureSetService {
  featureSets$ = new BehaviorSubject<FeatureSet[]>([]);
  availablePermissions$ = new BehaviorSubject<AvailablePermissions>([]);

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  loadFeatureSets() {
    return this.http
      .get<{ data: FeatureSet[] }>(`${environment.userBackend}/feature-sets`)
      .pipe(
        map((res) => res.data),
        tap((sets) => this.featureSets$.next(sets))
      );
  }

  loadAvailablePermissions() {
    return this.http
      .get<{ data: AvailablePermissions }>(
        `${environment.userBackend}/feature-sets/available-permissions`
      )
      .pipe(
        map((res) => res.data),
        tap((perms) => this.availablePermissions$.next(perms))
      );
  }

  createFeatureSet(name: string, permissions: string[]) {
    return this.http
      .post<{ data: FeatureSet }>(`${environment.userBackend}/feature-sets`, {
        name,
        permissions,
      })
      .pipe(
        map((res) => res.data),
        tap((fs) =>
          this.featureSets$.next([...this.featureSets$.value, fs])
        )
      );
  }

  updateFeatureSet(id: string, data: { name?: string; permissions?: string[] }) {
    return this.http
      .patch<{ data: FeatureSet }>(
        `${environment.userBackend}/feature-sets/${id}`,
        data
      )
      .pipe(
        map((res) => res.data),
        tap((updated) =>
          this.featureSets$.next(
            this.featureSets$.value.map((fs) =>
              fs.id === updated.id ? updated : fs
            )
          )
        )
      );
  }

  deleteFeatureSet(id: string) {
    return this.http
      .delete<{ message: string }>(
        `${environment.userBackend}/feature-sets/${id}`
      )
      .pipe(
        tap(() =>
          this.featureSets$.next(
            this.featureSets$.value.filter((fs) => fs.id !== id)
          )
        )
      );
  }

  getDisplayName(featureSet: FeatureSet): string {
    const translationKey = this.getDisplayNameTranslationKey(featureSet);
    if (!translationKey) {
      return featureSet.name;
    }

    const translated = this.translate.instant(translationKey);
    return translated === translationKey ? featureSet.name : translated;
  }

  private resetState() {
    this.featureSets$.next([]);
    this.availablePermissions$.next([]);
  }

  private getDisplayNameTranslationKey(featureSet: FeatureSet): string | null {
    const permissions = featureSet.permissions || [];
    const permission = permissions.length === 1 ? permissions[0] : null;

    if (!permission) {
      return null;
    }

    const normalizedName = (featureSet.name || '').trim().toLowerCase();
    const isKnownDefaultName =
      normalizedName === 'dozent' || normalizedName === 'instructor';

    if (!featureSet.is_default && !isKnownDefaultName) {
      return null;
    }

    return `enums.featureSetPermissions.${permission}.label`;
  }
}
